<template>
  <div class="step-employee">
    <div class="step-header">
      <h2>{{ t('Selecione o Funcionário') }}</h2>
      <p class="step-description">
        {{ t('Escolha o funcionário que receberá esta regra de pagamento.') }}
      </p>
    </div>

    <!-- Franchise Context -->
    <div class="context-banner">
      <i class="pi pi-building"></i>
      <div class="context-content">
        <strong>{{ t('Franquia selecionada:') }}</strong>
        <span>{{ selectedFranchiseName }}</span>
      </div>
    </div>

    <!-- Helper Banner -->
    <div class="helper-banner">
      <i class="pi pi-info-circle"></i>
      <div class="helper-content">
        <strong>{{ t('Lembre-se:') }}</strong>
        <ul>
          <li>{{ t('Cada funcionário pode ter múltiplas regras de pagamento') }}</li>
          <li>{{ t('As regras são somadas para calcular o pagamento total') }}</li>
          <li>{{ t('Após criar, não será possível alterar o funcionário') }}</li>
        </ul>
      </div>
    </div>

    <form @submit.prevent="handleNext" class="wizard-form">
      <!-- Employee Selection with ButtonsList -->
      <buttons-list
        v-if="employees.length > 0"
        data-testid="payroll-payment-rules-step2-employee-list"
        :itens-select="employees"
        v-model="wizardData.user_id"
        :label="t('Funcionários Disponíveis')"
        :placeholder="t('Selecione o funcionário')"
        elem-id="employee-selection"
        :ng-required="true"
        :callback="onEmployeeSelected"
        :search-enabled="true"
      />

      <div v-else class="field-info">
        <i class="pi pi-spin pi-spinner"></i>
        {{ t('Carregando funcionários...') }}
      </div>

      <!-- Selected Employee Preview -->
      <div v-if="selectedEmployee" class="employee-preview">
        <div class="preview-header">
          <i class="pi pi-user"></i>
          <div class="employee-info">
            <strong>{{ selectedEmployee.Name }}</strong>
            <small>{{ t('Funcionário selecionado') }}</small>
          </div>
        </div>

        <div v-if="existingRules.length > 0" class="existing-rules">
          <div class="rules-title">
            {{ t('Regras existentes:') }} {{ existingRules.length }}
          </div>
          <div class="rules-list">
            <div v-for="rule in existingRules.slice(0, 3)" :key="rule.id" class="rule-item">
              <span class="rule-badge">{{ getRuleTypeLabel(rule.rule_type) }}</span>
              <span class="rule-amount">{{ formatRuleAmount(rule) }}</span>
            </div>
            <div v-if="existingRules.length > 3" class="rules-more">
              +{{ existingRules.length - 3 }} {{ t('mais regras') }}
            </div>
          </div>
          <div class="rules-total">
            {{ t('Total atual:') }} {{ formatTotalExisting() }}
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, inject } from 'vue';
import type { Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useI18n as useI18nFormatter } from 'vue-i18n';
import ButtonsList from '@/inputs/ButtonsList.vue';
import { permissionsService } from '@/services/permissionsService';
import { log } from '@/utils/DebugLogger';

type Id = number | string;

interface WizardData {
  franchise_id?: Id;
  user_id?: Id;
  rule_type?: string;
  amount?: number;
  meta?: string | null;
  [key: string]: unknown;
}

interface PaymentRule {
  id: Id;
  user_id: Id;
  rule_type: string;
  amount: number | string;
  [key: string]: unknown;
}

interface WizardMachine {
  wizardData: Ref<WizardData>;
  existingRules?: Ref<PaymentRule[]>;
  next: () => void;
}

interface EmployeeOption {
  ID: Id;
  Name: string;
}

interface FranchiseOption {
  ID: Id;
  Name: string;
}

// Inject machine from parent
const machine = inject<WizardMachine | null>('wizardMachine', null);

const { t } = useI18n();
const { n } = useI18nFormatter();

// Use wizard data from machine
const wizardData = computed<WizardData>(() => machine?.wizardData.value || {});

const employees = ref<EmployeeOption[]>([]);

const canProceed = computed<boolean>(() => {
  return wizardData.value.user_id !== null && wizardData.value.user_id !== undefined;
});

const selectedEmployee = computed<EmployeeOption | null>(() => {
  if (!wizardData.value.user_id) return null;
  return employees.value.find(e => e.ID === wizardData.value.user_id) || null;
});

const selectedFranchiseName = computed<string>(() => {
  if (!wizardData.value.franchise_id) return t('Não selecionada');
  const franchises = permissionsService.getFranquiasSelectArray() as FranchiseOption[];
  const franchise = franchises.find(f => f.ID === wizardData.value.franchise_id);
  return franchise ? franchise.Name : t('Não encontrada');
});

// Get existing rules for selected employee
const existingRules = computed<PaymentRule[]>(() => {
  if (!wizardData.value.user_id) return [];
  return machine?.existingRules?.value?.filter(rule => rule.user_id === wizardData.value.user_id) || [];
});

const loadEmployees = (franchiseId: Id | undefined): void => {
  if (!franchiseId) {
    employees.value = [];
    log('Step2Employee', 'loadEmployees', 'No franchise selected, clearing employees');
    return;
  }

  log('Step2Employee', 'loadEmployees', 'Loading employees for franchise', { franchiseId });

  const funcionarios = permissionsService.getFuncionariosSelectArrayByFranquiaID(Number(franchiseId), String(wizardData.value.user_id ?? '')) as EmployeeOption[];
  employees.value = funcionarios.map(func => ({
    ID: func.ID,
    Name: func.Name
  }));

  log('Step2Employee', 'loadEmployees', 'Loaded employees', employees.value.length);
};

const onEmployeeSelected = (): void => {
  log('Step2Employee', 'onEmployeeSelected', 'Employee selected', wizardData.value.user_id);
};

const getRuleTypeLabel = (ruleType: string): string => {
  const labels: Record<string, string> = {
    'fixed': t('Salário Fixo'),
    'commission_over_gross_revenue_executed': t('Comissão Executada'),
    'commission_over_gross_revenue_sold_remotely': t('Comissão Remota'),
    'commission_over_gross_revenue_sold_in_loco': t('Comissão Presencial')
  };
  return labels[ruleType] || ruleType;
};

const formatRuleAmount = (rule: PaymentRule): string => {
  // PRESERVED: amount = currency for fixed; amount = fraction (0..1) for commission rules.
  if (rule.rule_type === 'fixed') {
    return n(parseFloat(String(rule.amount)), 'currency');
  }
  return `${Math.round(parseFloat(String(rule.amount)) * 100)}%`;
};

const formatTotalExisting = (): string => {
  const total = existingRules.value.reduce<number>((sum, rule) => {
    if (rule.rule_type === 'fixed') {
      return sum + parseFloat(String(rule.amount));
    }
    // For commission, can't calculate without revenue
    return sum;
  }, 0);

  if (total > 0) {
    return n(total, 'currency');
  }
  return t('Comissão: depende da receita');
};

const handleNext = (): void => {
  machine?.next();
};

// Load employees on mount if franchise is selected
onMounted(() => {
  if (wizardData.value.franchise_id) {
    loadEmployees(wizardData.value.franchise_id);
  }
});
</script>

<style scoped>
.step-employee {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.step-header h2 {
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-color);
}

.step-description {
  margin: 0;
  color: var(--text-color-secondary);
  font-size: 1rem;
}

.context-banner {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: var(--surface-100);
  border-left: 4px solid var(--primary-color);
  border-radius: 6px;
}

.context-banner i {
  color: var(--primary-color);
  font-size: 1.5rem;
  flex-shrink: 0;
}

.context-content {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.context-content strong {
  color: var(--text-color-secondary);
  font-size: 0.875rem;
}

.context-content span {
  color: var(--text-color);
  font-weight: 600;
  font-size: 1rem;
}

.helper-banner {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: var(--blue-50);
  border-left: 4px solid var(--blue-500);
  border-radius: 6px;
}

.helper-banner i {
  color: var(--blue-500);
  font-size: 1.5rem;
  flex-shrink: 0;
}

.helper-content strong {
  display: block;
  margin-bottom: 0.5rem;
  color: var(--blue-700);
}

.helper-content ul {
  margin: 0;
  padding-left: 1.25rem;
}

.helper-content li {
  margin-bottom: 0.25rem;
  color: var(--blue-900);
  font-size: 0.9rem;
}

.wizard-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.field-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: var(--surface-50);
  border: 1px dashed var(--surface-border);
  border-radius: 6px;
  color: var(--text-color-secondary);
  font-size: 0.9rem;
}

.employee-preview {
  padding: 1.25rem;
  background: var(--surface-50);
  border: 1px solid var(--surface-border);
  border-radius: 8px;
}

.preview-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding-bottom: 1rem;
  margin-bottom: 1rem;
  border-bottom: 1px solid var(--surface-border);
}

.preview-header i {
  font-size: 1.5rem;
  color: var(--primary-color);
}

.employee-info strong {
  display: block;
  color: var(--text-color);
  font-size: 1rem;
}

.employee-info small {
  color: var(--text-color-secondary);
  font-size: 0.875rem;
}

.existing-rules {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.rules-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-color);
}

.rules-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.rule-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background: var(--surface-0);
  border-radius: 4px;
  font-size: 0.875rem;
}

.rule-badge {
  padding: 0.25rem 0.5rem;
  background: var(--primary-color);
  color: white;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
}

.rule-amount {
  font-weight: 600;
  color: var(--text-color);
}

.rules-more {
  text-align: center;
  color: var(--text-color-secondary);
  font-size: 0.875rem;
  font-style: italic;
}

.rules-total {
  padding-top: 0.75rem;
  border-top: 1px solid var(--surface-border);
  font-weight: 600;
  color: var(--text-color);
  text-align: right;
}

.no-rules {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-color-secondary);
  font-size: 0.9rem;
  font-style: italic;
}
</style>
